import {
  Add,
  CheckCircle,
  HighlightOffRounded,
  MailOutline,
  Person,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";

import { useDict } from "../../utils/useDict";

import { LargeContentBox } from "../../components/common";
import { GenericTable } from "../../components/GenericTable/GenericTable";
import { useContractContext } from "../../contexts/ContractContext";
import { usePractitionerRoleContext } from "../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../contexts/SessionContext";
import { useFormatter } from "../../utils/useFormatter";
import { InviteDialog } from "./components/InviteDialog";
import { ManagePractitionerActions } from "./components/ManagePractitionersActions";
import { ChangeToCnpjDialog } from "./components/ChangeToCnpjDialog";
import { PageHeader } from "@/components/common/PageHeader";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { endpoints } from "@/configs/Settings";
import { isFreePlanContract } from "@/utils/contract";

const PRACTITIONER_ROLE_ASSOCIATION_URL =
  "https://fluxmed.cloud/StructureDefinition/PractitionerRoleAssociation";

type PractitionerRoleAssociation = {
  cnpjDigits: string;
  organizationAlias: string;
  isSocio: boolean;
};

function onlyDigits(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

function getAssociation(
  practitionerRole?: fhir5.PractitionerRole,
): PractitionerRoleAssociation | null {
  const ext = practitionerRole?.extension?.find(
    (e: any) => e?.url === PRACTITIONER_ROLE_ASSOCIATION_URL,
  ) as any;
  if (!ext || !Array.isArray(ext.extension)) return null;

  const cnpjDigits = onlyDigits(
    ext.extension?.find((x: any) => x?.url === "cnpj")?.valueString,
  );
  const organizationAlias = String(
    ext.extension?.find((x: any) => x?.url === "organizationAlias")
      ?.valueString || "",
  ).trim();
  const isSocio =
    ext.extension?.find((x: any) => x?.url === "isSocio")?.valueBoolean ===
    true;

  if (!cnpjDigits) return null;
  return {
    cnpjDigits,
    organizationAlias: organizationAlias || cnpjDigits,
    isSocio,
  };
}

export function ManagePractitioners() {
  const [isOpen, setIsOpen] = useState(false);
  const [isChangeToCnpjOpen, setIsChangeToCnpjOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("1");
  const [selectedAssociation, setSelectedAssociation] = useState("none");
  const { practitionerRole, practitioner } = usePractitionerRoleContext();
  const { typeOrganization, organization } = useOrganizationContext();
  const { user, access } = useSessionContext();

  const { currentContract } = useContractContext();
  const contract = currentContract?.[0]?.resource;
  const isFreePlan = isFreePlanContract(contract);

  const { formatNameDisplay } = useFormatter;

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const columns = [
    {
      column: "Profissional",
      rowName: "practitionerName",
    },
    { column: "Número no conselho de classe", rowName: "classId" },
    { column: "Atividade", rowName: "displayRoles" },
    { column: "Especialidade", rowName: "speciality" },
    // Na lista geral (e demais abas), exibe o CNPJ do vínculo de associação quando existir extensão.
    // { column: "CNPJ associado", rowName: "associationCnpjLabel" },
  ];

  const associationOptions = (() => {
    const map = new Map<
      string,
      { cnpjDigits: string; organizationAlias: string }
    >();
    practitionerRole?.forEach((entry: any) => {
      const assoc = getAssociation(entry?.resource);
      if (!assoc) return;
      if (!map.has(assoc.cnpjDigits)) {
        map.set(assoc.cnpjDigits, {
          cnpjDigits: assoc.cnpjDigits,
          organizationAlias: assoc.organizationAlias,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.organizationAlias.localeCompare(b.organizationAlias),
    );
  })();

  const hasAssociationGroups = associationOptions.length > 0;

  const isSocioViewOnly =
    access?.roles?.includes("associated") &&
    !access?.roles?.includes("owner") &&
    !access?.roles?.includes("manager") &&
    Boolean(access?.associatedCnpjDigits);

  // Se o usuário é sócio (view-only), forçamos a associação dele e ocultamos outras.
  const effectiveSelectedAssociation = isSocioViewOnly
    ? String(access?.associatedCnpjDigits || "")
    : selectedAssociation;

  // PractitionerRole do usuário logado (mesma org no contexto).
  const userOwnPractitionerRoleEntry = practitionerRole?.find((e: any) =>
    e?.resource?.id?.includes(user.username),
  );
  const userOwnAssociation = getAssociation(
    userOwnPractitionerRoleEntry?.resource,
  );

  /**
   * CNPJ da extensão PractitionerRoleAssociation com o qual restringimos a lista:
   * sócio, secretariado só com vínculo, ou qualquer perfil que não seja proprietário mas
   * tenha a extensão no próprio role (não vê profissionais de outro grupo de associação).
   * Proprietários sem essa regra extra continuam vendo todos na aba "Todos".
   */
  const viewerAssociationScopeCnpjDigits = (() => {
    if (isSocioViewOnly) return onlyDigits(access?.associatedCnpjDigits || "");
    if (access?.managerAssociationScopeCnpjDigits) {
      return onlyDigits(access.managerAssociationScopeCnpjDigits);
    }
    if (userOwnAssociation?.cnpjDigits && !access?.roles?.includes("owner")) {
      return userOwnAssociation.cnpjDigits;
    }
    return "";
  })();

  const filteredPractitionerRoles = practitionerRole?.filter((entry: any) => {
    const assoc = getAssociation(entry?.resource);
    if (viewerAssociationScopeCnpjDigits) {
      if (assoc?.cnpjDigits !== viewerAssociationScopeCnpjDigits) return false;
    }
    // "none" (aba inicial) significa "Todos" dentro do escopo já aplicado acima.
    if (effectiveSelectedAssociation === "none") return true;
    return assoc?.cnpjDigits === effectiveSelectedAssociation;
  });

  const userIsSocioOfSelectedAssociation = (() => {
    if (!user?.username) return false;
    if (
      !effectiveSelectedAssociation ||
      effectiveSelectedAssociation === "none"
    )
      return false;
    const userRoleEntry = practitionerRole?.find((e: any) =>
      e?.resource?.id?.includes(user.username),
    );
    const assoc = getAssociation(userRoleEntry?.resource);
    return assoc?.cnpjDigits === effectiveSelectedAssociation;
  })();

  // Abas por CNPJ: ocultas para sócio, secretário com escopo ou profissional com extensão (não proprietário).
  const hideAssociationCnpjTabs =
    isSocioViewOnly ||
    (Boolean(viewerAssociationScopeCnpjDigits) &&
      !access?.roles?.includes("owner"));

  const canInviteInSelectedAssociation =
    !hasAssociationGroups || effectiveSelectedAssociation === "none"
      ? true
      : userIsSocioOfSelectedAssociation;

  const rows = filteredPractitionerRoles?.map(
    (el: fhir5.BundleEntry<fhir5.PractitionerRole>) => {
      const rowAssociation = getAssociation(el?.resource);
      const associationCnpjLabel = rowAssociation
        ? `${rowAssociation.organizationAlias} (${useFormatter.formatCNPJ(
            rowAssociation.cnpjDigits,
          )})`
        : "—";

      const practitionerReference = practitioner?.find(
        (e: fhir5.BundleEntry<fhir5.Practitioner>) =>
          e?.resource?.id
            ? el?.resource?.practitioner?.reference?.includes(e?.resource?.id)
            : false,
      );

      const classId = useFormatter.formatConselho(
        el?.resource?.identifier?.[0]?.value || "",
      );
      const speciality =
        el?.resource?.specialty
          ?.map(
            (element: fhir5.CodeableConcept) => element?.coding?.[0]?.display,
          )
          ?.join(", ") || "-";
      const displayRoles =
        el?.resource?.code
          ?.map(
            (role: fhir5.CodeableConcept) =>
              useDict.roles[role?.coding?.[0]?.system || ""],
          )
          ?.filter((el: any) => el !== undefined)
          ?.join(", ") || "-";

      const roles = el?.resource?.code?.map(
        (el: fhir5.CodeableConcept) => el?.coding?.[0]?.system,
      );

      const situation =
        el?.resource?.active === false
          ? "Inativo"
          : displayRoles?.includes("Convidado")
            ? "Convidado"
            : "Ativo";
      return {
        practitionerName: formatNameDisplay(
          practitionerReference?.resource?.name?.[0]?.text || "-",
        ),
        practitionerId: practitionerReference?.resource?.id || "-",
        classId,
        displayRoles: displayRoles?.replace(/\bConvidado\b(, )?/g, ""),
        roles,
        speciality,
        situation,
        associationCnpjLabel,
      };
    },
  );

  // Se não houver contrato ativo, tratamos como plano gratuito padrão:
  // - CPF (bus): 1 profissional (o próprio usuário)
  // - Organização (cnpj): 3 profissionais (plano gratuito)
  const defaultMaxPractitioners = typeOrganization === "bus" ? 1 : 3;
  const maxPractitioners = currentContract?.[0]?.resource?.term?.[0]?.identifier
    ?.value
    ? parseInt(
        currentContract?.[0]?.resource?.term?.[0]?.identifier?.value || "",
      )
    : defaultMaxPractitioners;

  const countPractitioner = practitionerRole
    ? maxPractitioners -
      practitionerRole?.filter((el: any) => el?.resource?.active !== false)
        ?.length
    : 0;
  const limitPractitioners =
    (practitionerRole &&
      practitionerRole?.filter((el: any) => el?.resource?.active !== false)
        ?.length >= maxPractitioners) ||
    false;

  const handleUpgradePlan = () => {
    localStorage.setItem("operation", "renewPlan");
    localStorage.setItem("organizationValue", `${organization?.id}`);
    window.location.replace(`${endpoints.REGISTER}`);
  };

  // Exibe aviso quando o tipo de organização é 'bus' (CPF)

  return (
    <LargeContentBox>
      <Stack width="100%" spacing={2}>
        <PageHeader
          title="Configurar profissionais"
          icon={Person}
          action={
            typeOrganization !== "bus" ? (
              limitPractitioners ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  justifyContent="center"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography variant="body1">
                    {isFreePlan
                      ? "Você atingiu o limite do plano gratuito. Para cadastrar mais profissionais, faça um upgrade no plano."
                      : "Você atingiu o limite de profissionais do seu plano. Para cadastrar mais, faça um upgrade no plano."}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={handleUpgradePlan}
                  >
                    Fazer upgrade
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => setIsOpen(true)}
                  disabled={!canInviteInSelectedAssociation}
                >
                  Enviar convite
                </Button>
              )
            ) : (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Typography variant="body1">
                  Para convidar outros profissionais, é necessário alterar o
                  cadastro da organização para incluir um CNPJ.
                </Typography>
                <Stack spacing={0.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    disabled={false}
                    onClick={() => setIsChangeToCnpjOpen(true)}
                  >
                    Alterar cadastro para CNPJ
                  </Button>
                </Stack>
              </Stack>
            )
          }
        />

        {hasAssociationGroups && !hideAssociationCnpjTabs && (
          <Stack spacing={1}>
            <Typography variant="body2" color="neutral700.main">
              Selecione a associação (CNPJ) para gerenciar os profissionais.
            </Typography>
            <Tabs
              value={selectedAssociation}
              onChange={(_, value) => setSelectedAssociation(String(value))}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="none" label="Todos" />
              {associationOptions.map((o) => (
                <Tab
                  key={o.cnpjDigits}
                  value={o.cnpjDigits}
                  label={`${o.organizationAlias} (${useFormatter.formatCNPJ(
                    o.cnpjDigits,
                  )})`}
                />
              ))}
            </Tabs>

            {!canInviteInSelectedAssociation && (
              <Typography variant="body2" color="neutral700.main">
                Para convidar profissionais para esta associação, é necessário
                ser sócio do CNPJ selecionado.
              </Typography>
            )}
          </Stack>
        )}

        {hideAssociationCnpjTabs && viewerAssociationScopeCnpjDigits && (
          <Typography variant="body2" color="neutral700.main">
            Lista restrita ao CNPJ associado ao seu perfil:{" "}
            {useFormatter.formatCNPJ(viewerAssociationScopeCnpjDigits)}.
          </Typography>
        )}

        <TabContext value={selectedTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <TabList onChange={handleChangeTab}>
              <Tab
                style={{ display: "flex", alignItems: "center" }}
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <CheckCircle sx={{ marginRight: 1 }} />
                    Ativos
                  </Box>
                }
                value="1"
              />

              <Tab
                style={{ display: "flex", alignItems: "center" }}
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <MailOutline sx={{ marginRight: 1 }} />
                    Convidados
                  </Box>
                }
                value="2"
              />
              <Tab
                style={{ display: "flex", alignItems: "center" }}
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <HighlightOffRounded sx={{ marginRight: 1 }} />
                    Inativos
                  </Box>
                }
                value="3"
              />
            </TabList>

            {!currentContract ? (
              <Typography variant="body1">
                Você está no plano gratuito. Para adicionar mais profissionais,
                faça um upgrade do plano.
              </Typography>
            ) : countPractitioner > 0 ? (
              <Typography variant="body1">
                {countPractitioner === 1
                  ? `Ainda há ${countPractitioner} vaga disponível`
                  : `Ainda há ${countPractitioner} vagas disponíveis`}
              </Typography>
            ) : (
              <Typography variant="body1">
                Limite de profissionais atingido
              </Typography>
            )}
          </Box>
          <TabPanel value="1">
            <GenericTable
              tableColumns={columns}
              tableRows={
                rows?.filter((el: any) => el?.situation === "Ativo") || []
              }
              actions={(rowData: any) => (
                <ManagePractitionerActions row={rowData} />
              )}
            />
          </TabPanel>
          <TabPanel value="2">
            <GenericTable
              tableColumns={columns}
              tableRows={
                rows?.filter((el: any) => el?.situation === "Convidado") || []
              }
              actions={(rowData: any) => (
                <ManagePractitionerActions row={rowData} />
              )}
            />
          </TabPanel>
          <TabPanel value="3">
            <GenericTable
              tableColumns={columns}
              tableRows={
                rows?.filter((el: any) => el?.situation === "Inativo") || []
              }
              actions={(rowData: any) => (
                <ManagePractitionerActions row={rowData} />
              )}
            />
          </TabPanel>
        </TabContext>
      </Stack>

      {isOpen && (
        <InviteDialog
          practitioners={filteredPractitionerRoles ?? practitionerRole}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          associationOptions={associationOptions}
          defaultAssociationCnpjDigits={
            effectiveSelectedAssociation !== "none"
              ? effectiveSelectedAssociation
              : ""
          }
        />
      )}

      {isChangeToCnpjOpen && (
        <ChangeToCnpjDialog
          isOpen={isChangeToCnpjOpen}
          setIsOpen={setIsChangeToCnpjOpen}
        />
      )}
    </LargeContentBox>
  );
}
